import type { HouseholdMember } from '@awwyeah/shared';
import type { CSSProperties } from 'react';
import { memberTint, textOnMemberColor } from '../../theme/memberColor';

/**
 * Member colour coding.
 *
 * A member's mark is their own chosen `color` hex (household-doc profile, the WS-2 profile
 * editor), applied inline. It used to be mapped onto one of four `--m-faye/dan/ezry/kora`
 * design tokens by position in the id-sorted roster — usability testing (2026-08-01) found
 * that scheme actively misleading: Dan and Faye landed in adjacent slots and rendered
 * nearly identically, and everyone's slot (and therefore colour) reshuffled whenever the
 * roster order changed. Real hex, not a slot, is the only thing that stays "theirs". The
 * initial still rides along everywhere (design: "Color alone never carries meaning"), so
 * this is additive, not a replacement for legibility.
 */

export interface MemberStyle {
  /** Month-grid dot / week bar fill. */
  dot: CSSProperties;
  /** Left edge of an agenda pill or detail card. */
  edge: CSSProperties;
  /** Soft background for all-day strips and chips. */
  tint: CSSProperties;
  /** Text in the member's colour (attribution lines, the active filter-chip label). */
  text: CSSProperties;
  /** Solid fill for avatars, paired with a legible white/ink label colour. */
  fill: CSSProperties;
}

function styleForColor(hex: string): MemberStyle {
  return {
    dot: { backgroundColor: hex },
    edge: { borderLeftColor: hex },
    tint: { backgroundColor: memberTint(hex) },
    text: { color: hex },
    fill: { backgroundColor: hex, color: textOnMemberColor(hex) },
  };
}

/** Neutral treatment for an owner we have no profile for (yet, or ever). Token-based, not a
 * member hex — there is no "their colour" to render. */
export const NEUTRAL_STYLE: MemberStyle = {
  dot: { backgroundColor: 'var(--app-fg-3)' },
  edge: { borderLeftColor: 'var(--app-fg-3)' },
  tint: { backgroundColor: 'var(--app-raised)' },
  text: { color: 'var(--app-fg-3)' },
  fill: { backgroundColor: 'var(--app-fg-3)', color: 'var(--app-surface)' },
};

/**
 * Nobody's event — imported feed entries and anything saved with an empty `ownerIds`.
 *
 * Usability testing (2026-08-01, Faye): a solid grey dot reads as "some fifth person",
 * which is exactly wrong for "this belongs to the household". Unassigned therefore gets a
 * *hollow, dashed* mark — no filled colour at all — and the legend names it. Kept distinct
 * from `NEUTRAL_STYLE`, which still means "a real owner whose profile hasn't synced yet".
 * The dashed ring is a shape, not a colour, so it stays a class — pair `UNASSIGNED_STYLE`
 * with `UNASSIGNED_DOT_CLASS` wherever the hollow mark itself is drawn.
 */
export const UNASSIGNED_STYLE: MemberStyle = {
  dot: { backgroundColor: 'transparent' },
  edge: { borderLeftColor: 'var(--app-fg-3)' },
  tint: { backgroundColor: 'var(--app-raised)' },
  text: { color: 'var(--app-fg-3)' },
  fill: { backgroundColor: 'var(--app-raised)', color: 'var(--app-fg-3)' },
};

/** The hollow dashed ring itself — combine with `UNASSIGNED_STYLE.dot` (transparent fill). */
export const UNASSIGNED_DOT_CLASS = 'border border-dashed border-fg-3';

export interface MemberView extends HouseholdMember {
  style: MemberStyle;
  initial: string;
}

export function initialOf(name: string): string {
  return (name.trim()[0] ?? '?').toUpperCase();
}

/** Stable ordering (by id), so a member's position in lists is consistent across devices —
 * colour itself no longer depends on this order at all. */
export function toMemberViews(members: readonly HouseholdMember[]): MemberView[] {
  return [...members]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((member) => ({
      ...member,
      style: styleForColor(member.color),
      initial: initialOf(member.name),
    }));
}

export function memberIndex(members: readonly MemberView[]): Map<string, MemberView> {
  return new Map(members.map((member) => [member.id, member]));
}

/**
 * The style an event should wear: its first known owner, else neutral — except an event
 * with *no* owners at all, which wears the hollow "unassigned" mark (see UNASSIGNED_STYLE).
 */
export function styleForOwners(
  ownerIds: readonly string[],
  byId: ReadonlyMap<string, MemberView>,
): MemberStyle {
  if (ownerIds.length === 0) return UNASSIGNED_STYLE;
  for (const ownerId of ownerIds) {
    const member = byId.get(ownerId);
    if (member) return member.style;
  }
  return NEUTRAL_STYLE;
}

/** "Ezry · Faye" — the attribution line under an event title. */
export function ownerNames(
  ownerIds: readonly string[],
  byId: ReadonlyMap<string, MemberView>,
): string {
  const names = ownerIds.map((id) => byId.get(id)?.name).filter((name): name is string => !!name);
  return names.join(' · ');
}
